import { Navigate, useNavigate } from "react-router-dom";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";
import { FileText, Download, Clock, CheckCircle2, AlertTriangle, ShieldAlert, ArrowRight, FileArchive, Layers, BarChart3, AlertCircle, Info, Box, Calendar, TrendingUp, ShieldCheck } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { exportDashboardPdf } from "@/lib/export-pdf";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { useEffect, useMemo, useState } from "react";
import { maskName } from "@/lib/format";

const COLORS = {
  primary: "#1A66CC",
  success: "#10B981", 
  warning: "#F59E0B", 
  danger: "#EF4444",  
};



export default function DashboardPage() {
  const { dashboard, duracaoMs, notas, divergencias, privacyMode } = useFiscalStore();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [volumetriaDays, setVolumetriaDays] = useState<number>(31);

  useEffect(() => { document.title = "Dashboard — Extrator Fiscal"; }, []);

  const stats = dashboard?.stats || { totalNotas: 0, totalRevenue: 0 };
  const taxCards = dashboard?.taxCards || [];
  const riskSummary = dashboard?.riskSummary || { alta: 0, media: 0, baixa: 0 };

  const totalRevenue = stats.totalRevenue;
  const totalNotas = stats.totalNotas;
  const ticketMedio = totalNotas > 0 ? totalRevenue / totalNotas : 0;
  const totalTributos = taxCards.reduce((acc, curr) => acc + curr.totalValue, 0);
  const cargaTributaria = totalRevenue > 0 ? (totalTributos / totalRevenue) * 100 : 0;

  const memoData = useMemo(() => {
    let entradas = 0;
    let saidas = 0;
    const diasMap = new Map<string, { date: string; Entrada: number; Saida: number; Canceladas: number }>();
    const cfopMap = new Map<string, { cfop: string; vContabil: number; icms: number; fcp: number; icmsST: number; fcpST: number; }>();
    const prodMap = new Map<string, { nome: string; vContabil: number; vImpostos: number; qCom: number; }>();
    let economia = 0;

    let ncmGenerico = 0;
    let eanInvalido = 0;
    let semCest = 0;
    let totalItens = 0;
    const anomaliasCfopMap = new Map<string, number>();

    for (const nota of notas) {
      const cfopStr = nota.cfopPrincipal || "";
      const isEntrada = cfopStr.startsWith("1") || cfopStr.startsWith("2") || cfopStr.startsWith("3");
      const isSaida = cfopStr.startsWith("5") || cfopStr.startsWith("6") || cfopStr.startsWith("7");
      
      if (isEntrada) entradas++;
      else if (isSaida) saidas++;
      else if (nota.valorOficial >= 0) saidas++;

      if (nota.emissao) {
        const d = nota.emissao.split("T")[0];
        const dayKey = d.slice(-2);
        if (!diasMap.has(dayKey)) diasMap.set(dayKey, { date: dayKey, Entrada: 0, Saida: 0, Canceladas: 0 });
        const row = diasMap.get(dayKey)!;
        if (nota.notaStatus === "CANCELADA" || nota.notaStatus === "INUTILIZADA") row.Canceladas++;
        else if (isEntrada) row.Entrada++;
        else row.Saida++;
      }

      if (nota.notaStatus !== "CANCELADA" && nota.notaStatus !== "INUTILIZADA" && nota.itens) {
        for (const item of nota.itens) {
          totalItens++;
          const c = item.cfop || nota.cfopPrincipal || "Outros";
          if (!cfopMap.has(c)) cfopMap.set(c, { cfop: c, vContabil: 0, icms: 0, fcp: 0, icmsST: 0, fcpST: 0 });
          const cRow = cfopMap.get(c)!;
          cRow.vContabil += item.vProd;
          
          cRow.icms += (item.impostos?.icms?.vICMS || 0);
          cRow.fcp += (item.impostos?.fcp?.vFCP || 0);
          cRow.icmsST += (item.impostos?.icmsST?.vICMSST || 0);
          cRow.fcpST += (item.impostos?.fcpST?.vFCPST || 0);

          // Lógica de Saneamento Técnico
          const ncm = (item.ncm || "").replace(/\D/g, "");
          if (ncm === "00000000" || ncm === "99999999" || ncm === "") {
            ncmGenerico++;
            anomaliasCfopMap.set(c, (anomaliasCfopMap.get(c) || 0) + 1);
          }
          const ean = (item.produtoExtra?.cEAN || "").toUpperCase();
          if (!ean || ean === "SEM GTIN" || ean === "0") {
            eanInvalido++;
          }
          if (c.startsWith("54") || c.startsWith("14")) { // CFOPs de ST
             if (!item.produtoExtra?.CEST) semCest++;
          }
        }
      }
    }

    divergencias.forEach(d => { 
      if (d.diferenca < 0) economia += Math.abs(d.diferenca); 
    });

    const topAnomaliasCfop = Array.from(anomaliasCfopMap.entries())
      .map(([cfop, count]) => ({ cfop, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      notasEntrada: entradas,
      notasSaida: saidas,
      chartPeriodo: Array.from(diasMap.values()).sort((a, b) => a.date.localeCompare(b.date)),
      cfopData: Array.from(cfopMap.values()).sort((a, b) => a.cfop.localeCompare(b.cfop)),
      topAnomaliasCfop,
      saneamento: {
        totalItens,
        ncmGenerico,
        eanInvalido,
        semCest,
        score: totalItens > 0 ? Math.max(0, 100 - ((ncmGenerico + eanInvalido) / totalItens * 100)) : 100
      },
      economiaEstimada: economia,
    };
  }, [notas, divergencias]);

  const totCfop = memoData.cfopData.reduce((acc, r) => {
    acc.vContabil += r.vContabil;
    acc.icms += r.icms;
    acc.fcp += r.fcp;
    acc.icmsST += r.icmsST;
    acc.fcpST += r.fcpST;
    return acc;
  }, { vContabil: 0, icms: 0, fcp: 0, icmsST: 0, fcpST: 0 });

  const pieData = [
    { name: "Autorizadas", value: dashboard?.statusSummary.autorizadas.quantidade || 0, color: COLORS.success },
    { name: "Autorizada em Contingência", value: dashboard?.statusSummary.contingencia.quantidade || 0, color: COLORS.warning },
    { name: "Canceladas", value: dashboard?.statusSummary.canceladas.quantidade || 0, color: COLORS.danger },
  ].filter(d => d.value > 0);

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => {
      try {
        if (dashboard) exportDashboardPdf(dashboard, notas, duracaoMs);
        toast.success("Relatório PDF gerado.");
      } catch (e: any) { toast.error("Falha ao gerar PDF."); }
      finally { setExporting(false); }
    }, 50);
  };

  if (!dashboard) return <Navigate to="/upload" replace />;

  return (
    <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[1600px] mx-auto space-y-6 lg:space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-3xl font-extrabold tracking-tight dark:text-white">Processamento Sistêmico de Dados</h1>
          <p className="text-xs lg:text-sm text-muted-foreground mt-1">Visão 360º dos indicadores técnicos e integridade do período.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-3 px-4 py-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
            <Clock className="size-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-medium text-blue-900 dark:text-blue-200">Sessão Ativa ({fmtNum(totalNotas)} notas)</span>
          </div>
          <Button onClick={handleExport} disabled={exporting} className="w-full md:w-auto rounded-xl gap-2 shadow-lg shadow-blue-500/20">
            {exporting ? <Clock className="size-4 animate-spin" /> : <Download className="size-4" />} Exportar Dashboard
          </Button>
        </div>
      </header>

      {/* KPIs com Estilo Glass Uniformizado */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
        <div className="animate-in-card" style={{ animationDelay: '100ms' }}>
          <KpiCard label="Volume Total" value={fmtNum(totalNotas)} sub="Documentos" icon={FileText} color="text-slate-500" />
        </div>
        <div className="animate-in-card" style={{ animationDelay: '200ms' }}>
          <KpiCard label="Receita Bruta" value={fmtBRL(totalRevenue)} sub="Total Notas" icon={CheckCircle2} color="text-emerald-500" />
        </div>
        <div className="animate-in-card" style={{ animationDelay: '300ms' }}>
          <KpiCard label="Ticket Médio" value={fmtBRL(ticketMedio)} sub="Por nota" icon={TrendingUp} color="text-blue-500" />
        </div>
        <div className="animate-in-card" style={{ animationDelay: '400ms' }}>
          <KpiCard label="Entradas" value={fmtNum(memoData.notasEntrada)} sub={`${((memoData.notasEntrada/totalNotas)*100).toFixed(0)}% Vol`} icon={Download} color="text-amber-500" />
        </div>
        <div className="animate-in-card" style={{ animationDelay: '500ms' }}>
          <KpiCard label="Saídas" value={fmtNum(memoData.notasSaida)} sub={`${((memoData.notasSaida/totalNotas)*100).toFixed(0)}% Vol`} icon={Box} color="text-emerald-500" />
        </div>
      </div>

      {/* Tributos Consolidados - Estilo Glass */}
      <div className="glass-card p-4 lg:p-6 animate-in-card hover-glow" style={{ animationDelay: '600ms' }}>
        <div className="flex items-center justify-between mb-4 lg:mb-6">
          <h2 className="text-base lg:text-lg font-bold flex items-center gap-2"><Layers className="size-5 text-primary" /> Tributos Consolidados</h2>
          <span className="hidden sm:inline text-[10px] uppercase font-bold text-muted-foreground bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">Visão Centavos</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {taxCards.map(t => {
            const perc = totalTributos > 0 ? (t.totalValue / totalTributos) * 100 : 0;
            return (
              <div 
                key={t.title} 
                className={cn(
                  "p-3 rounded-xl border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group",
                  isDark 
                    ? "bg-white/5 border-white/10 hover:bg-primary/10 hover:border-primary/40" 
                    : "bg-white border-slate-200 shadow-sm hover:border-primary/30"
                )}
              >
                <div className="flex flex-col h-full relative">
                  <div className="absolute -left-3 top-0 bottom-0 w-1 rounded-r-full bg-primary/20 group-hover:bg-primary transition-colors" />
                  
                  <div className={cn(
                    "text-[10px] uppercase font-bold mb-1 truncate",
                    isDark ? "text-slate-400" : "text-slate-500"
                  )}>
                    {t.title}
                  </div>
                  <div className={cn(
                    "text-sm lg:text-base font-black mb-1 tabular-nums",
                    isDark ? "text-white" : "text-slate-900"
                  )}>
                    {fmtBRL(t.totalValue)}
                  </div>
                  <div className="text-[9px] text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                    <TrendingUp className="size-2.5" />
                    {perc.toFixed(2)}% <span className="opacity-60 font-medium">Part.</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 pt-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span className="text-xs lg:text-sm font-medium text-muted-foreground text-center sm:text-left">Carga Tributária Total Estimada:</span>
          <span className="text-lg lg:text-xl font-black text-emerald-500">{fmtBRL(totalTributos)} ({cargaTributaria.toFixed(2)}%)</span>
        </div>
      </div>

      {/* Gráficos em Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass-card p-4 lg:p-6 relative flex flex-col animate-in-card hover-glow" style={{ animationDelay: '700ms' }}>
          <h2 className="text-base lg:text-lg font-bold mb-6 flex items-center gap-2"><PieChart className="size-5 text-primary" /> Status Operacional</h2>
          <div className="flex-1 min-h-[220px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} innerRadius={65} outerRadius={85} dataKey="value" stroke="none" paddingAngle={5}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl lg:text-3xl font-black dark:text-white">{fmtNum(totalNotas)}</span>
              <span className="text-[10px] text-muted-foreground font-bold uppercase">Total</span>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {pieData.map(d => (
              <div key={d.name} className="text-center">
                <div className="size-1.5 rounded-full mx-auto mb-1" style={{ backgroundColor: d.color }}></div>
                <div className="text-[9px] font-bold text-muted-foreground uppercase truncate">{d.name}</div>
                <div className="text-xs font-black dark:text-slate-200">{fmtNum(d.value)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 glass-card p-4 lg:p-6 flex flex-col animate-in-card hover-glow" style={{ animationDelay: '800ms' }}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base lg:text-lg font-bold flex items-center gap-2"><Calendar className="size-5 text-primary" /> Volumetria por Período</h2>
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-lg border border-white/10">
              {[7, 15, 31].map((d) => (
                <button
                  key={d}
                  onClick={() => setVolumetriaDays(d)}
                  className={cn(
                    "px-3 py-1 text-[9px] font-black uppercase tracking-tighter rounded-md transition-all",
                    volumetriaDays === d 
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20" 
                      : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                  )}
                >
                  {d === 31 ? "Mês" : `${d}D`}
                </button>
              ))}
            </div>
          </div>
          <div className="h-[250px] flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={memoData.chartPeriodo.slice(-volumetriaDays)}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-10" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                <Line type="monotone" dataKey="Entrada" stroke={COLORS.success} strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="Saida" stroke={COLORS.primary} strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="Canceladas" stroke={COLORS.danger} strokeWidth={2} strokeDasharray="5 5" dot={false} />
                <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Dados de ICMS extraídos - Estilo Ledger Premium */}
      <div className="glass-card overflow-hidden border-blue-500/10 shadow-2xl animate-in-card" style={{ animationDelay: '900ms' }}>
        <div className="p-5 lg:p-7 border-b border-white/10 bg-gradient-to-r from-blue-500/5 to-transparent flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]">
              <FileText className="size-5 text-blue-500" />
            </div>
            <div>
              <h2 className="text-lg lg:text-xl font-black tracking-tight dark:text-white">Cálculo Estimado de Imposto</h2>
              <p className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.2em] mt-0.5">Consolidado por CFOP · Visão Técnica</p>
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Modelo G/A - 1.0</span>
            <span className="text-xs font-mono text-blue-500 font-bold">{new Date().toLocaleDateString("pt-BR")}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] lg:text-xs text-right border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-slate-950 to-blue-900/40 border-b border-white/10 text-blue-300">
                <th className="p-4 text-left font-black text-[9px] uppercase tracking-[0.2em] border-r border-white/10">CFOP</th>
                <th className="p-4 font-black text-[9px] uppercase tracking-[0.2em] border-r border-white/10">Valor Contábil</th>
                <th className="p-4 text-center font-black text-[9px] uppercase tracking-[0.2em] border-r border-white/10 bg-blue-500/5">ICMS Próprio</th>
                <th className="p-4 text-center font-black text-[9px] uppercase tracking-[0.2em] border-r border-white/10 bg-indigo-500/5">FCP</th>
                <th className="p-4 text-center font-black text-[9px] uppercase tracking-[0.2em] border-r border-white/10 bg-emerald-500/5">ICMS-ST</th>
                <th className="p-4 text-center font-black text-[9px] uppercase tracking-[0.2em] bg-emerald-600/5">FCP-ST</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {memoData.cfopData.map((row, i) => {
                const saldo = 0;
                return (
                  <tr key={i} className="hover:bg-blue-500/5 transition-all duration-200 group">
                    <td className="p-4 text-left border-r border-white/5">
                      <span className="px-2 py-1 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400 font-black tracking-tighter">
                        {row.cfop}
                      </span>
                    </td>
                    <td className="p-4 tabular-nums font-bold text-slate-400 border-r border-white/5 group-hover:text-white transition-colors">{fmtBRL(row.vContabil)}</td>
                    <td className="p-4 tabular-nums text-blue-400 font-black border-r border-white/5">{fmtBRL(row.icms)}</td>
                    <td className="p-4 tabular-nums text-indigo-400 font-medium border-r border-white/5">{fmtBRL(row.fcp)}</td>
                    <td className="p-4 tabular-nums text-emerald-400 font-black border-r border-white/5">{fmtBRL(row.icmsST)}</td>
                    <td className="p-4 tabular-nums text-emerald-500/80 font-medium">{fmtBRL(row.fcpST)}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="bg-blue-500/5 border-t-2 border-blue-500/20">
                <td className="p-6 text-left font-black text-blue-400 uppercase tracking-[0.2em] border-r border-white/5">Totais de Levantamento</td>
                <td className="p-6 tabular-nums font-black text-slate-300 border-r border-white/5">{fmtBRL(totCfop.vContabil)}</td>
                <td className="p-6 tabular-nums text-blue-400 font-black border-r border-white/5">{fmtBRL(totCfop.icms)}</td>
                <td className="p-6 tabular-nums text-indigo-400 font-bold border-r border-white/5">{fmtBRL(totCfop.fcp)}</td>
                <td className="p-6 tabular-nums text-emerald-400 font-black border-r border-white/5">{fmtBRL(totCfop.icmsST)}</td>
                <td className="p-6 tabular-nums text-emerald-500 font-bold">{fmtBRL(totCfop.fcpST)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Indicadores Técnicos e Saneamento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in-card" style={{ animationDelay: '1000ms' }}>
        {/* Card 1: Saneamento de Cadastro */}
        <div className="glass-card p-6 flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <ShieldCheck className="size-5" />
              </div>
              <h2 className="text-lg font-bold">Qualidade Cadastral</h2>
            </div>
            <div className="text-right">
              <span className="text-2xl font-black text-emerald-500">{memoData.saneamento.score.toFixed(0)}%</span>
              <p className="text-[8px] uppercase font-bold text-muted-foreground">Health Score</p>
            </div>
          </div>
          
          <div className="space-y-5 flex-1">
            <SaneamentoItem label="NCMs Genéricos / Inválidos" value={memoData.saneamento.ncmGenerico} total={memoData.saneamento.totalItens} color="bg-amber-500" />
            <SaneamentoItem label="EANs não informados (GTIN)" value={memoData.saneamento.eanInvalido} total={memoData.saneamento.totalItens} color="bg-blue-500" />
            <SaneamentoItem label="Produtos sem CEST (Obrigatório ST)" value={memoData.saneamento.semCest} total={memoData.saneamento.totalItens} color="bg-indigo-500" />
          </div>
          <p className="text-[10px] text-muted-foreground mt-6 text-center italic">
            Métricas baseadas no saneamento técnico dos itens extraídos.
          </p>
        </div>

        {/* Card 2: Top CFOPs por Incidência Técnica */}
        <div className="glass-card p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-6">
            <div className="size-10 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500">
              <AlertTriangle className="size-5" />
            </div>
            <h2 className="text-lg font-bold">Incidências por Operação</h2>
          </div>
          <div className="space-y-4 flex-1">
            {memoData.topAnomaliasCfop.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2 opacity-50">
                <CheckCircle2 className="size-8" />
                <span className="text-xs font-bold uppercase tracking-widest">Nenhuma anomalia técnica</span>
              </div>
            )}
            {memoData.topAnomaliasCfop.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-white/5 hover:border-orange-500/30 transition-all">
                <div className="flex items-center gap-3">
                  <span className="px-2 py-0.5 rounded text-[10px] font-black bg-orange-500/20 text-orange-400 border border-orange-500/30">{item.cfop}</span>
                  <span className="text-xs font-bold dark:text-slate-300">CFOP da Operação</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-black dark:text-white tabular-nums">{item.count}</div>
                  <div className="text-[9px] font-bold text-orange-500 uppercase">Alertas</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-4 lg:p-6 flex flex-col">
          <h2 className="text-base lg:text-lg font-bold mb-6 flex items-center gap-2"><AlertCircle className="size-5 text-amber-500" /> Indicadores Técnicos</h2>
          <div className="space-y-4 flex-1">
            <MetricRow icon={ArrowRight} color="emerald" title="Levantamento Identificado" value={fmtBRL(memoData.economiaEstimada)} desc="Valores identificados para revisão técnica." />
            <MetricRow icon={AlertTriangle} color="amber" title="Indícios de Inconsistência" value={fmtNum(divergencias.length)} desc="Notas com divergências no XML." />
            <MetricRow icon={ShieldAlert} color="red" title="Avisos Críticos" value={fmtNum(riskSummary.alta)} desc="Risco de malha técnica detectado." />
          </div>
          <Button variant="ghost" className="w-full mt-6 text-xs font-bold gap-2 text-primary hover:bg-primary/5" onClick={() => navigate("/inspecao")}>
            Inspecionar Malha de Dados <ArrowRight className="size-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color }: { label: string; value: string; sub: string; icon: any; color: string }) {
  return (
    <div className="glass-card p-5 hover-lift group">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg group-hover:bg-primary group-hover:text-white transition-colors">
          <Icon className="size-4" />
        </div>
        <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">{label}</span>
      </div>
      <div className={`text-2xl font-black tabular-nums dark:text-white ${color}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

function SaneamentoItem({ label, value, total, color }: { label: string, value: number, total: number, color: string }) {
  const perc = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-end">
        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight">{label}</span>
        <span className="text-xs font-black dark:text-white tabular-nums">{value} <span className="text-[9px] font-medium opacity-50">({perc.toFixed(1)}%)</span></span>
      </div>
      <div className="h-1.5 w-full bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
        <div className={cn("h-full transition-all duration-1000", color)} style={{ width: `${perc}%` }} />
      </div>
    </div>
  );
}

function MetricRow({ icon: Icon, color, title, value, desc }: any) {
  const colors: any = {
    emerald: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    red: "bg-red-500/10 text-red-500 border-red-500/20",
  };
  return (
    <div className={`p-3 rounded-xl border flex gap-3 ${colors[color]}`}>
      <div className="mt-1"><Icon className="size-4" /></div>
      <div>
        <div className="text-[10px] font-black uppercase opacity-80">{title}</div>
        <div className="text-base font-black tabular-nums">{value}</div>
        <div className="text-[9px] opacity-70 leading-tight">{desc}</div>
      </div>
    </div>
  );
}
